import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NextLink = () => {
  const { church } = useAuth();
  return church
    ? <Link to="/billing" className="btn-navy inline-block mt-8" data-testid="back-to-billing-btn">Back to billing</Link>
    : <Link to="/onboarding" className="btn-navy inline-block mt-8" data-testid="continue-setup-btn">Choose a plan</Link>;
};

const Shell = ({ icon, title, body, testid }) => (
  <div className="min-h-screen bg-paper flex items-center justify-center px-6">
    <div className="card-soft p-12 max-w-md text-center fade-up" data-testid={testid}>
      <div className="flex justify-center">{icon}</div>
      <h1 className="font-display text-navy text-3xl font-semibold mt-5">{title}</h1>
      <p className="text-muted-ink mt-3">{body}</p>
      <NextLink />
    </div>
  </div>
);

const useConfirm = (check) => {
  const [state, setState] = useState("pending");
  const { refresh } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    let tries = 0;
    const tick = async () => {
      try {
        if (await check()) { setState("paid"); refresh(); return; }
      } catch { /* retry */ }
      if (++tries < 15) setTimeout(tick, 2000); else setState("failed");
    };
    tick();
  }, [check, refresh]);
  return state;
};

const Result = ({ state }) =>
  state === "paid" ? <Shell testid="payment-success" icon={<CheckCircle2 size={48} className="text-teal" />} title="You're all set" body="Your Dream Teams subscription is active. Thank you for building your church's Dream Team." />
    : state === "failed" ? <Shell testid="payment-failed" icon={<XCircle size={48} className="text-red-700" />} title="We couldn't confirm your payment" body="If you were charged, it may take a minute to show up. Please check your billing page." />
      : <Shell testid="payment-pending" icon={<Loader2 size={48} className="text-teal animate-spin" />} title="Confirming your payment" body="This only takes a moment..." />;

export function PaymentSuccess() {
  const [p] = useSearchParams();
  const sid = p.get("session_id");
  const state = useConfirm(useRef(async () => (await api.get(`/payments/status/${sid}`)).data.payment_status === "paid").current);
  return <Result state={state} />;
}

export function PaypalReturn() {
  const [p] = useSearchParams();
  const sub = p.get("subscription_id");
  const state = useConfirm(useRef(async () => (await api.post("/billing/paypal/confirm", { subscription_id: sub })).data.status === "active").current);
  return <Result state={state} />;
}

export function PaymentCancel() {
  return <Shell testid="payment-cancel" icon={<XCircle size={48} className="text-muted-ink" />} title="Checkout canceled" body="No charge was made. You can pick a plan whenever you're ready." />;
}
